import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import { USER_TYPES, SENTIMENTS, STATUSES, STATUS_STYLES } from "@/lib/options";
import {
  LogOut, Download, Search, Flag, Filter, Inbox, X, Sparkles, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

const StatCard = ({ label, value, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-5">
    <div className="text-xs uppercase tracking-widest text-slate-400">{label}</div>
    <div className="mt-1 font-mono text-3xl font-medium text-ink">{value}</div>
  </div>
);

const maskEmail = (e) => {
  if (!e || !e.includes("@")) return e || "—";
  const [local, domain] = e.split("@");
  const shown = local.slice(0, 2);
  return `${shown}${"•".repeat(Math.max(3, local.length - 2))}@${domain}`;
};

function MaskedEmail({ email }) {
  const [reveal, setReveal] = useState(false);
  if (!email) return <span className="text-slate-400">—</span>;
  return (
    <button
      type="button"
      data-testid="admin-email-reveal"
      onClick={() => setReveal((r) => !r)}
      className="text-left text-navy underline decoration-dotted underline-offset-2 hover:text-cardinal"
      title={reveal ? "Click to hide" : "Click to reveal"}
    >
      {reveal ? email : maskEmail(email)}
    </button>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, flagged: 0, ai_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState({
    user_type: ALL, sentiment: ALL, status: ALL, contact_permission: ALL,
    city: "", date_from: "", date_to: "", search: "",
  });

  const buildParams = useCallback(() => {
    const p = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v && v !== ALL) p[k] = v;
    });
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [subs, st] = await Promise.all([
        api.get("/admin/submissions", { params: buildParams() }),
        api.get("/admin/stats"),
      ]);
      setRows(subs.data);
      setStats(st.data);
    } catch (e) {
      toast.error("Failed to load submissions.");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const resetFilters = () =>
    setFilters({
      user_type: ALL, sentiment: ALL, status: ALL, contact_permission: ALL,
      city: "", date_from: "", date_to: "", search: "",
    });

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/submissions/${id}/status`, { status });
      setRows((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected && selected.id === id) setSelected({ ...selected, status });
      toast.success("Status updated.");
    } catch (e) {
      toast.error("Failed to update status.");
    }
  };

  const resummarize = async (id) => {
    toast.info("Re-running AI summary…");
    try {
      await api.post(`/admin/submissions/${id}/resummarize`);
      await load();
      const fresh = (await api.get("/admin/submissions", { params: buildParams() })).data.find((r) => r.id === id);
      if (fresh) setSelected(fresh);
      toast.success("AI summary updated.");
    } catch (e) {
      toast.error("Re-summarization failed.");
    }
  };

  const exportCsv = async () => {
    try {
      const qs = new URLSearchParams(buildParams()).toString();
      const res = await fetch(`${API}/admin/submissions/export?${qs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "rental_experiences.csv";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported.");
    } catch (e) {
      toast.error("Export failed.");
    }
  };

  const doLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

  const FilterSelect = ({ k, placeholder, options, testid }) => (
    <Select value={filters[k]} onValueChange={(v) => setF(k, v)}>
      <SelectTrigger data-testid={testid} className="rounded-none border-slate-200 bg-white">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o} value={o}>{o}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA] font-body text-ink">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div>
            <div className="font-serif text-lg font-medium tracking-tight">
              Research Dashboard
            </div>
            <div className="mt-2 flex gap-1">
              <Link to="/admin" data-testid="admin-nav-experiences"
                className="border-b-2 border-navy px-3 py-1.5 text-sm text-ink">
                Experiences
              </Link>
              <Link to="/admin/criteria" data-testid="admin-nav-criteria"
                className="border-b-2 border-transparent px-3 py-1.5 text-sm text-slate-500 hover:text-ink">
                Criteria Database
              </Link>
            </div>
          </div>
          <Button
            data-testid="logout-button"
            onClick={doLogout}
            variant="outline"
            className="rounded-none border-ink"
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign Out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-8">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <StatCard testid="stat-total" label="Total Experiences" value={stats.total} />
          <StatCard testid="stat-new" label="New" value={stats.new} />
          <StatCard testid="stat-flagged" label="Flagged (PII)" value={stats.flagged} />
          <StatCard testid="stat-pending" label="AI Pending" value={stats.ai_pending} />
        </div>

        {/* filters */}
        <div className="mt-8 border border-slate-200 bg-white p-5">
          <div className="mb-4 flex items-center justify-between">
            <span className="flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-slate-500">
              <Filter className="h-4 w-4" /> Filters
            </span>
            <Button
              data-testid="reset-filters-button"
              variant="ghost" size="sm" onClick={resetFilters}
              className="rounded-none text-slate-500 hover:text-cardinal"
            >
              <X className="mr-1 h-4 w-4" /> Reset
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="relative sm:col-span-2 lg:col-span-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="search-input"
                value={filters.search}
                onChange={(e) => setF("search", e.target.value)}
                placeholder="Search stories / summaries…"
                className="rounded-none border-slate-200 bg-white pl-9"
              />
            </div>
            <Input
              data-testid="city-filter"
              value={filters.city}
              onChange={(e) => setF("city", e.target.value)}
              placeholder="City / County"
              className="rounded-none border-slate-200 bg-white"
            />
            <FilterSelect k="user_type" placeholder="All roles" options={USER_TYPES} testid="filter-role" />
            <FilterSelect k="sentiment" placeholder="All sentiments" options={SENTIMENTS} testid="filter-sentiment" />
            <FilterSelect k="status" placeholder="All statuses" options={STATUSES} testid="filter-status" />
            <Select value={filters.contact_permission} onValueChange={(v) => setF("contact_permission", v)}>
              <SelectTrigger data-testid="filter-contact" className="rounded-none border-slate-200 bg-white">
                <SelectValue placeholder="Contact permission" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Contact permission</SelectItem>
                <SelectItem value="yes">Contact: Yes</SelectItem>
                <SelectItem value="no">Contact: No</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex gap-2">
              <Input data-testid="date-from" type="date" value={filters.date_from}
                onChange={(e) => setF("date_from", e.target.value)}
                className="rounded-none border-slate-200 bg-white" />
              <Input data-testid="date-to" type="date" value={filters.date_to}
                onChange={(e) => setF("date_to", e.target.value)}
                className="rounded-none border-slate-200 bg-white" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <span className="text-sm text-slate-500" data-testid="result-count">
            {rows.length} {rows.length === 1 ? "result" : "results"}
          </span>
          <Button
            data-testid="export-csv-button"
            onClick={exportCsv}
            className="rounded-none bg-ink font-medium text-white hover:bg-slate-800"
          >
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
        </div>

        <div className="mt-3 overflow-x-auto border border-slate-200 bg-white">
          <table className="w-full border-collapse text-sm" data-testid="submissions-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">AI Summary</th>
                <th className="px-4 py-3">Sentiment</th>
                <th className="px-4 py-3">Flags</th>
                <th className="px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-slate-400">
                    <Inbox className="mx-auto mb-3 h-10 w-10" />
                    No submissions match your filters yet.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`submission-row-${r.id}`}
                    onClick={() => setSelected(r)}
                    className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50"
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-medium">{r.user_type}</td>
                    <td className="px-4 py-3">{r.city || "—"}</td>
                    <td className="px-4 py-3 max-w-[320px]">
                      {r.ai_processed ? (
                        <span className="line-clamp-2 text-slate-700">{r.ai_summary}</span>
                      ) : (
                        <span className="text-slate-400">Processing…</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{r.sentiment || r.ai_sentiment || "—"}</td>
                    <td className="px-4 py-3">
                      {r.flags && r.flags.length > 0 ? (
                        <span className="inline-flex items-center gap-1 text-cardinal">
                          <Flag className="h-3.5 w-3.5" /> {r.flags.length}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="outline" className={`rounded-none border ${STATUS_STYLES[r.status]}`}>
                        {r.status}
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-none border-slate-200" data-testid="submission-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-medium tracking-tight">
                  Experience Detail
                </DialogTitle>
                <DialogDescription className="text-slate-500">
                  AI-structured research data with the auto-redacted source story.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-6 text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <Info label="Role" value={selected.user_type} />
                  <Info label="City" value={selected.city || "—"} />
                  <Info label="Stated sentiment" value={selected.sentiment || "—"} />
                  <Info label="Submitted" value={fmtDate(selected.created_at)} />
                </div>

                {/* AI block */}
                <div className="border border-slate-200 bg-[#FAFAFA] p-5">
                  <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-cardinal">
                      <Sparkles className="h-4 w-4" /> AI structured summary
                    </span>
                    <button
                      data-testid="resummarize-button"
                      onClick={() => resummarize(selected.id)}
                      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-ink"
                    >
                      <RefreshCw className="h-3.5 w-3.5" /> Re-run
                    </button>
                  </div>
                  {selected.ai_processed ? (
                    <div className="space-y-4">
                      <p className="leading-relaxed text-ink">{selected.ai_summary}</p>
                      <div className="grid grid-cols-2 gap-4">
                        <Info label="AI sentiment" value={selected.ai_sentiment || "—"} />
                        <Info label="Outcome" value={selected.ai_outcome || "—"} />
                        <Info label="Issue type" value={selected.ai_issue_type || "—"} />
                      </div>
                      <Tags label="Topics" items={selected.ai_topics} />
                      <Tags label="Attributes" items={selected.ai_attributes} />
                      <Tags label="Potential policy topics" items={selected.ai_policy_topics} />
                    </div>
                  ) : (
                    <p className="text-slate-400">
                      AI summary is still processing or unavailable. Use “Re-run”.
                    </p>
                  )}
                </div>

                {selected.flags && selected.flags.length > 0 && (
                  <Tags label="Moderation flags (auto-redacted)" items={selected.flags} cardinal />
                )}

                <div className="border border-slate-200 p-4">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="text-xs font-medium uppercase tracking-widest text-slate-500">
                      Contact details
                    </div>
                    {selected.contact_permission ? (
                      <span className="text-xs text-emerald-600">Follow-up permitted</span>
                    ) : (
                      <span className="text-xs text-slate-400">No follow-up consent</span>
                    )}
                  </div>
                  <Info label="Name" value={selected.contact_name || "—"} />
                  <div className="flex items-baseline gap-2 py-1">
                    <span className="w-20 shrink-0 text-xs uppercase tracking-widest text-slate-400">Email</span>
                    <MaskedEmail email={selected.contact_email} />
                  </div>
                  <Info label="Phone" value={selected.contact_phone || "—"} />
                  {selected.report_opt_in && (
                    <div className="mt-2 text-xs text-calblue">✓ Opted in to annual report</div>
                  )}
                </div>

                <div>
                  <div className="mb-1 text-xs font-medium uppercase tracking-widest text-slate-500">
                    Source story (auto-redacted)
                  </div>
                  <p className="whitespace-pre-wrap border border-slate-200 bg-white p-4 leading-relaxed text-ink">
                    {selected.story}
                  </p>
                </div>

                <div>
                  <div className="mb-2 text-xs font-medium uppercase tracking-widest text-slate-500">
                    Review status
                  </div>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger data-testid="status-update-select" className="rounded-none border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((st) => (
                        <SelectItem key={st} value={st}>{st}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Info = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
    <div className="font-medium text-ink">{value}</div>
  </div>
);

const Tags = ({ label, items, cardinal }) => {
  if (!items || items.length === 0) return null;
  return (
    <div>
      <div className="mb-2 text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="flex flex-wrap gap-2">
        {items.map((t) => (
          <span
            key={t}
            className={`rounded-none border px-2 py-1 text-xs ${
              cardinal ? "border-cardinal text-cardinal" : "border-slate-300 text-slate-600"
            }`}
          >
            {t}
          </span>
        ))}
      </div>
    </div>
  );
};
